import md5 from 'js-md5';

const STORAGE_KEY = 'mail-translation-cache-v1';
const TEXT_STORAGE_KEY = 'mail-translation-text-cache-v1';
const MAX_CACHE_SIZE = 30;
const MAX_TEXT_CACHE_SIZE = 500;

function getStorage() {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }

  return window.localStorage;
}

function readCacheStore(storageKey = STORAGE_KEY) {
  const storage = getStorage();
  if (!storage) {
    return {};
  }

  try {
    return JSON.parse(storage.getItem(storageKey) || '{}');
  } catch (error) {
    console.error('read translation cache failed:', error);
    return {};
  }
}

function writeCacheStore(store, storageKey = STORAGE_KEY) {
  const storage = getStorage();
  if (!storage) {
    return;
  }

  try {
    storage.setItem(storageKey, JSON.stringify(store));
  } catch (error) {
    console.error('write translation cache failed:', error);
  }
}

function buildCacheKey(emailId, language) {
  return `${String(emailId || '')}::${String(language || '')}`;
}

function buildTextCacheKey(source, target, textDigest) {
  return `${String(source || 'auto')}::${String(target || '')}::${String(textDigest || '')}`;
}

function pruneStore(store, maxCacheSize) {
  const keys = Object.keys(store);
  if (keys.length <= maxCacheSize) {
    return store;
  }

  keys
    .sort((left, right) => (store[left]?.updatedAt || 0) - (store[right]?.updatedAt || 0))
    .slice(0, keys.length - maxCacheSize)
    .forEach((key) => {
      delete store[key];
    });

  return store;
}

export function getTranslationCache({ emailId, language, digest }) {
  if (!emailId || !language || !digest) {
    return null;
  }

  const store = readCacheStore(STORAGE_KEY);
  const cacheKey = buildCacheKey(emailId, language);
  const cacheItem = store[cacheKey];

  if (!cacheItem || cacheItem.translatedDigest !== digest) {
    return null;
  }

  return cacheItem;
}

export function setTranslationCache({ emailId, language, translatedHtml, translatedDigest }) {
  if (!emailId || !language || !translatedHtml || !translatedDigest) {
    return;
  }

  const store = readCacheStore(STORAGE_KEY);
  const cacheKey = buildCacheKey(emailId, language);

  store[cacheKey] = {
    emailId: String(emailId),
    language: String(language),
    translatedHtml,
    translatedDigest,
    updatedAt: Date.now(),
  };

  pruneStore(store, MAX_CACHE_SIZE);

  writeCacheStore(store, STORAGE_KEY);
}

export function getTextTranslationCache({ source = 'auto', target, text }) {
  const safeTarget = String(target || '').trim();
  const safeText = String(text || '').trim();
  if (!safeTarget || !safeText) {
    return null;
  }

  const store = readCacheStore(TEXT_STORAGE_KEY);
  const textDigest = md5(safeText);
  const cacheKey = buildTextCacheKey(source, safeTarget, textDigest);
  const cacheItem = store[cacheKey];

  if (!cacheItem || cacheItem.textDigest !== textDigest) {
    return null;
  }

  return cacheItem.translatedText || null;
}

export function setTextTranslationCache({ source = 'auto', target, text, translatedText }) {
  const safeTarget = String(target || '').trim();
  const safeText = String(text || '').trim();
  const safeTranslatedText = String(translatedText || '');
  if (!safeTarget || !safeText || !safeTranslatedText) {
    return;
  }

  const store = readCacheStore(TEXT_STORAGE_KEY);
  const textDigest = md5(safeText);
  const cacheKey = buildTextCacheKey(source, safeTarget, textDigest);

  store[cacheKey] = {
    source: String(source || 'auto'),
    target: safeTarget,
    textDigest,
    translatedText: safeTranslatedText,
    updatedAt: Date.now(),
  };

  pruneStore(store, MAX_TEXT_CACHE_SIZE);

  writeCacheStore(store, TEXT_STORAGE_KEY);
}
